import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import CameraScannerModal from '@/components/pos/CameraScannerModal';
import { posApi } from '@/services/api';
import { POSItem, ItemVariant, BarcodeScanResult, isCompanyAdmin, isManager, ROLES } from '@/types';
import { getCurrencySymbol } from '@/utils/currency';
import {
  Tag, Barcode, Search, X, Loader2, Layers,
  AlertCircle, Menu, CornerDownRight, ChevronRight, Check,
} from 'lucide-react';

type PriceForm = {
  sale_price: string;
  delivery_price: string;
  wholesale_price: string;
  reseller_price: string;
  minimum_price: string;
  retail_minimum_price: string;
  cost_price: string;
};

const EMPTY_PRICES: PriceForm = {
  sale_price: '',
  delivery_price: '',
  wholesale_price: '',
  reseller_price: '',
  minimum_price: '',
  retail_minimum_price: '',
  cost_price: '',
};

const PRICE_KEYS: (keyof PriceForm)[] = [
  'sale_price',
  'delivery_price',
  'wholesale_price',
  'reseller_price',
  'minimum_price',
  'retail_minimum_price',
  'cost_price',
];

function numToForm(v: number | undefined | null): string {
  if (v === undefined || v === null || Number.isNaN(Number(v))) return '';
  return String(v);
}

function pricesFromItem(item: POSItem, variant?: ItemVariant | null): PriceForm {
  if (variant) {
    return {
      sale_price: numToForm(variant.price),
      delivery_price: numToForm(variant.delivery_price),
      wholesale_price: numToForm(variant.wholesale_price),
      reseller_price: numToForm(variant.reseller_price),
      minimum_price: numToForm(variant.minimum_price),
      retail_minimum_price: numToForm(variant.retail_minimum_price),
      cost_price: numToForm(variant.cost_price),
    };
  }
  return {
    sale_price: numToForm(item.sale_price),
    delivery_price: numToForm(item.delivery_price),
    wholesale_price: numToForm(item.wholesale_price),
    reseller_price: numToForm(item.reseller_price),
    minimum_price: numToForm(item.minimum_price),
    retail_minimum_price: numToForm(item.retail_minimum_price),
    cost_price: numToForm(item.cost_price),
  };
}

function parsePrice(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const n = parseFloat(trimmed);
  if (Number.isNaN(n)) return undefined;
  return n;
}

export default function PriceUpdatePage() {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();
  const { role, permissions } = useAuthStore();
  const currency = getCurrencySymbol();

  const canAccess = useMemo(() => {
    if (isCompanyAdmin(role) || isManager(role)) return true;
    if (role === ROLES.CASHIER && permissions?.can_update_item_prices) return true;
    return false;
  }, [role, permissions]);

  const [searchQuery, setSearchQuery] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [scanPending, setScanPending] = useState(false);
  const [scanError, setScanError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [results, setResults] = useState<POSItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<POSItem | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<ItemVariant | null>(null);
  const [prices, setPrices] = useState<PriceForm>(EMPTY_PRICES);
  const [baseline, setBaseline] = useState<PriceForm>(EMPTY_PRICES);

  const searchInputRef = useRef<HTMLInputElement>(null);

  /** Keep barcode field ready for wedge/gun scans — only autofocus this field. */
  const focusBarcode = useCallback((opts?: { select?: boolean }) => {
    const run = () => {
      const el = searchInputRef.current;
      if (!el || el.disabled) return;
      el.focus({ preventScroll: true });
      if (opts?.select !== false) {
        el.select();
      }
    };
    // After save/re-render the sticky button / toast can steal focus; retry briefly.
    requestAnimationFrame(run);
    setTimeout(run, 50);
    setTimeout(run, 200);
    setTimeout(run, 400);
  }, []);

  useEffect(() => {
    focusBarcode();
  }, [focusBarcode]);

  const applySelection = useCallback((item: POSItem, variant?: ItemVariant | null) => {
    const next = pricesFromItem(item, variant);
    setSelectedItem(item);
    setSelectedVariant(variant || null);
    setPrices(next);
    setBaseline(next);
    setResults([]);
    // Leave barcode focused so the next gun scan is ready after user saves.
    // Clear query so the next scan does not append to the previous code.
    setSearchQuery('');
  }, []);

  const handleCloseScanner = () => {
    setShowScanner(false);
    focusBarcode();
  };

  const handleBarcodeLookup = useCallback(async (code: string): Promise<boolean> => {
    if (!code) return false;
    setScanPending(true);
    setScanError('');
    setSelectedItem(null);
    setSelectedVariant(null);
    setResults([]);
    setPrices(EMPTY_PRICES);
    setBaseline(EMPTY_PRICES);

    try {
      const res = await posApi.scanBarcode(code);
      const result = res.data.message as BarcodeScanResult;

      const parentRes = await posApi.getItems({ search: result.item_code || result.item, limit: 5 });
      const parentItems = parentRes.data.message.items as POSItem[];
      const parentItem =
        parentItems.find(
          (i) => i.name === result.item || i.item_code === result.item || i.item_code === result.item_code,
        ) || parentItems[0];

      if (parentItem) {
        const matchedVariant = result.variant
          ? parentItem.variants?.find((v) => v.name === result.variant) || null
          : null;
        applySelection(parentItem, matchedVariant);
      } else {
        const mockItem: POSItem = {
          name: result.item,
          item_code: result.item_code || result.item,
          item_name: result.item_name,
          category: result.category || 'N/A',
          item_type: result.item_type,
          sale_price: result.rate,
          wholesale_price: result.wholesale_price,
          delivery_price: result.delivery_price,
          reseller_price: result.reseller_price,
          minimum_price: result.minimum_price,
          retail_minimum_price: result.retail_minimum_price,
          stock_quantity: result.stock_quantity,
          barcode: result.barcode,
          has_variants: false,
          is_active: true,
        };
        applySelection(mockItem, null);
      }
      return true;
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Product barcode not found';
      setScanError(typeof msg === 'string' ? msg : 'Product not found');
      return false;
    } finally {
      setScanPending(false);
    }
  }, [applySelection]);

  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (!trimmed) return;

    if (trimmed.length < 3) {
      setScanError(t('priceUpdate.searchMinChars'));
      return;
    }

    setLoading(true);
    setScanError('');
    setSelectedItem(null);
    setSelectedVariant(null);
    setResults([]);
    setPrices(EMPTY_PRICES);
    setBaseline(EMPTY_PRICES);

    try {
      const barcodeRes = await posApi.scanBarcode(trimmed);
      const result = barcodeRes.data.message as BarcodeScanResult;

      const parentRes = await posApi.getItems({ search: result.item_code || result.item, limit: 5 });
      const parentItems = parentRes.data.message.items as POSItem[];
      const parentItem =
        parentItems.find(
          (i) => i.name === result.item || i.item_code === result.item || i.item_code === result.item_code,
        ) || parentItems[0];

      if (parentItem) {
        const matchedVariant = result.variant
          ? parentItem.variants?.find((v) => v.name === result.variant) || null
          : null;
        applySelection(parentItem, matchedVariant);
      } else {
        const mockItem: POSItem = {
          name: result.item,
          item_code: result.item_code || result.item,
          item_name: result.item_name,
          category: result.category || 'N/A',
          item_type: result.item_type,
          sale_price: result.rate,
          wholesale_price: result.wholesale_price,
          delivery_price: result.delivery_price,
          reseller_price: result.reseller_price,
          minimum_price: result.minimum_price,
          retail_minimum_price: result.retail_minimum_price,
          stock_quantity: result.stock_quantity,
          barcode: result.barcode,
          has_variants: false,
          is_active: true,
        };
        applySelection(mockItem, null);
      }
    } catch {
      try {
        const res = await posApi.getItems({ search: trimmed, limit: 30 });
        const items = res.data.message.items as POSItem[];
        setResults(items);
        if (items.length === 1) {
          applySelection(items[0], null);
        } else if (items.length === 0) {
          setScanError(t('priceUpdate.noMatch', { query: trimmed }));
        }
      } catch (searchErr: any) {
        const msg = searchErr.response?.data?.message || 'Error searching for product';
        setScanError(typeof msg === 'string' ? msg : 'Search failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setScanError('');
    setSelectedItem(null);
    setSelectedVariant(null);
    setResults([]);
    setPrices(EMPTY_PRICES);
    setBaseline(EMPTY_PRICES);
    focusBarcode();
  };

  const selectResultItem = (item: POSItem) => {
    applySelection(item, null);
  };

  const selectVariant = (variant: ItemVariant) => {
    if (!selectedItem) return;
    applySelection(selectedItem, variant);
  };

  const setPriceField = (key: keyof PriceForm, value: string) => {
    setPrices((prev) => ({ ...prev, [key]: value }));
  };

  const dirty = useMemo(
    () => PRICE_KEYS.some((k) => prices[k].trim() !== baseline[k].trim()),
    [prices, baseline],
  );

  const handleSave = async () => {
    if (!selectedItem || !dirty) return;

    if (selectedItem.has_variants && !selectedVariant) {
      toast.error(t('priceUpdate.selectVariantFirst'));
      return;
    }

    const changes: Record<string, number> = {};
    for (const key of PRICE_KEYS) {
      if (prices[key].trim() === baseline[key].trim()) continue;
      const parsed = parsePrice(prices[key]);
      if (parsed === undefined) {
        toast.error(t('priceUpdate.invalidPrice'));
        return;
      }
      if (parsed < 0) {
        toast.error(t('priceUpdate.negativePrice'));
        return;
      }
      changes[key] = parsed;
    }

    if (!Object.keys(changes).length) return;

    setSaving(true);
    try {
      const payload: {
        name: string;
        variant?: string;
        sale_price?: number;
        wholesale_price?: number;
        reseller_price?: number;
        minimum_price?: number;
        retail_minimum_price?: number;
        cost_price?: number;
        delivery_price?: number;
      } = {
        name: selectedItem.name,
        ...changes,
      };
      if (selectedVariant) {
        payload.variant = selectedVariant.name;
      }

      const res = await posApi.bulkUpdateItemPrices([payload]);
      const result = res.data.message as {
        updated_count?: number;
        errors?: Array<{ message?: string }>;
        skipped?: Array<{ reason?: string }>;
      };

      if (result.errors?.length) {
        toast.error(result.errors[0].message || t('priceUpdate.saveFailed'));
        return;
      }
      if (!result.updated_count) {
        const reason = result.skipped?.[0]?.reason;
        if (reason === 'has_variants') {
          toast.error(t('priceUpdate.selectVariantFirst'));
        } else {
          toast.error(t('priceUpdate.saveFailed'));
        }
        return;
      }

      toast.success(t('priceUpdate.saved'));
      setSearchQuery('');
      setScanError('');
      setSelectedItem(null);
      setSelectedVariant(null);
      setPrices(EMPTY_PRICES);
      setBaseline(EMPTY_PRICES);
      setResults([]);
      // Refocus after React drops the save bar / re-enables the input.
      setTimeout(() => focusBarcode(), 0);
    } catch (err: any) {
      const msg = err.response?.data?.message || t('priceUpdate.saveFailed');
      toast.error(typeof msg === 'string' ? msg : t('priceUpdate.saveFailed'));
    } finally {
      setSaving(false);
      setTimeout(() => focusBarcode(), 50);
    }
  };

  if (!canAccess) {
    return <Navigate to="/" replace />;
  }

  const fieldLabel = (key: keyof PriceForm) => {
    const map: Record<keyof PriceForm, string> = {
      sale_price: t('priceUpdate.salePrice'),
      delivery_price: t('priceUpdate.deliveryPrice'),
      wholesale_price: t('priceUpdate.wholesalePrice'),
      reseller_price: t('priceUpdate.resellerPrice'),
      minimum_price: t('priceUpdate.minimumPrice'),
      retail_minimum_price: t('priceUpdate.retailMinimumPrice'),
      cost_price: t('priceUpdate.costPrice'),
    };
    return map[key];
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto flex flex-col min-h-0">
        <TrialBanner />

        <div className="p-6 max-w-4xl w-full mx-auto flex-1 flex flex-col min-h-0">
          <div className="flex items-center gap-3 mb-6 shrink-0">
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Tag className="w-6 h-6 text-primary-600" /> {t('priceUpdate.title')}
              </h1>
              <p className="text-gray-500 text-sm">{t('priceUpdate.subtitle')}</p>
            </div>
          </div>

          <div className="card p-4 mb-6 shrink-0 shadow-md border-primary-100 bg-white/80 backdrop-blur-sm">
            <form onSubmit={handleSearchSubmit} className="flex gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  inputMode="search"
                  enterKeyHint="search"
                  autoFocus
                  autoComplete="off"
                  placeholder={t('priceUpdate.placeholder')}
                  className="input-field pl-10 pr-10 py-3 text-base w-full focus:ring-primary-500"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={loading || scanPending || saving}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary px-6 flex items-center gap-2 text-base font-semibold shadow-sm"
                disabled={loading || scanPending || saving}
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('common.search')}
              </button>

              <button
                type="button"
                onClick={() => setShowScanner(true)}
                className="flex items-center justify-center p-3 rounded-lg border-2 border-primary-500 text-primary-600 bg-primary-50 hover:bg-primary-100 hover:border-primary-600 transition-all font-semibold"
                title={t('priceUpdate.scanCamera')}
                disabled={loading || scanPending || saving}
              >
                <Barcode className="w-6 h-6" />
              </button>
            </form>
          </div>

          {scanError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-800 animate-in fade-in shrink-0">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">{t('priceUpdate.searchFailed')}</p>
                <p className="text-xs text-red-700 mt-0.5">{scanError}</p>
              </div>
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-auto pb-8">
            {loading && (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
                <Loader2 className="w-12 h-12 text-primary-500 animate-spin" />
                <p className="text-gray-500 text-sm mt-3 font-medium">{t('priceUpdate.fetching')}</p>
              </div>
            )}

            {!loading && !selectedItem && results.length === 0 && !scanError && (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-gray-200 text-center px-4">
                <div className="w-16 h-16 bg-primary-50 text-primary-600 rounded-full flex items-center justify-center mb-4">
                  <Tag className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{t('priceUpdate.emptyTitle')}</h3>
                <p className="text-gray-500 text-sm max-w-sm">{t('priceUpdate.emptyHelp')}</p>
              </div>
            )}

            {!loading && results.length > 1 && (
              <div className="space-y-3 animate-in fade-in">
                <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                  <span>{t('priceUpdate.foundMatches', { count: results.length })}</span>
                  <span>{t('priceChecker.selectOne')}</span>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {results.map((item) => (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => selectResultItem(item)}
                      className="w-full text-left card p-4 flex items-center justify-between hover:border-primary-400 hover:shadow-md transition-all bg-white group"
                    >
                      <div className="min-w-0 pr-3">
                        <h4 className="font-bold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                          {item.item_name}
                        </h4>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {item.item_code}
                          {item.barcode ? ` · ${item.barcode}` : ''}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary-500 shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!loading && selectedItem && (
              <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-300 pb-4">
                <div className="card overflow-hidden bg-white shadow-lg border-primary-50">
                  <div className="bg-primary-900 text-white p-6 relative">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-xs font-bold bg-white/20 px-2.5 py-1 rounded-full uppercase tracking-wider">
                        {selectedItem.category}
                      </span>
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full border border-white/30 uppercase tracking-wider ${
                        selectedItem.item_type === 'Product' ? 'bg-emerald-500/20 text-emerald-100' : 'bg-indigo-500/20 text-indigo-100'
                      }`}>
                        {selectedItem.item_type}
                      </span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight">
                      {selectedItem.item_name}
                    </h2>
                    <p className="text-white/60 text-xs sm:text-sm mt-1 font-mono">
                      {selectedItem.item_code}
                      {selectedItem.barcode ? ` · ${selectedItem.barcode}` : ''}
                    </p>
                  </div>

                  {!!selectedItem.has_variants && !!selectedItem.variants?.length && (
                    <div className="p-6 border-b border-gray-100 space-y-3">
                      <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider flex items-center gap-1.5">
                        <Layers className="w-4 h-4 text-primary-500" />
                        {t('priceUpdate.selectVariant')}
                      </h3>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {selectedItem.variants.map((v) => {
                          const active = selectedVariant?.name === v.name;
                          return (
                            <button
                              key={v.name}
                              type="button"
                              onClick={() => selectVariant(v)}
                              className={`text-left p-4 rounded-xl border min-h-[48px] transition-all ${
                                active
                                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500/20 shadow-md'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-semibold text-gray-900 flex items-center gap-1.5">
                                  <CornerDownRight className="w-4 h-4 text-gray-400" />
                                  {v.variant_name}
                                </span>
                                {active && <Check className="w-4 h-4 text-primary-600" />}
                              </div>
                              {v.barcode && (
                                <p className="text-xs text-gray-400 font-mono pl-5 mt-0.5">{v.barcode}</p>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {(!selectedItem.has_variants || !!selectedVariant) && (
                    <div className="p-6">
                      <div className="grid gap-4 sm:grid-cols-2">
                        {PRICE_KEYS.map((key) => {
                          const isSale = key === 'sale_price';
                          return (
                            <div key={key} className={isSale ? 'sm:col-span-2' : undefined}>
                              <label className={`block font-semibold text-gray-700 mb-1.5 ${isSale ? 'text-sm' : 'text-xs'}`}>
                                {fieldLabel(key)}
                              </label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                                  {currency}
                                </span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className={`input-field pl-8 w-full ${isSale ? 'py-3.5 text-xl font-bold min-h-[56px]' : 'py-3 text-base min-h-[48px]'}`}
                                  value={prices[key]}
                                  onChange={(e) => setPriceField(key, e.target.value)}
                                  disabled={saving}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={!dirty || saving}
                        className="btn-primary w-full mt-6 min-h-[52px] text-base font-bold flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm"
                      >
                        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                        {t('priceUpdate.savePrices')}
                      </button>
                    </div>
                  )}

                  {!!selectedItem.has_variants && !selectedVariant && (
                    <div className="p-4 text-sm text-amber-700 bg-amber-50 border-t border-amber-100">
                      {t('priceUpdate.selectVariantFirst')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showScanner && (
        <CameraScannerModal
          onScan={handleBarcodeLookup}
          onClose={handleCloseScanner}
          scanPending={scanPending}
          scanError={scanError}
        />
      )}
    </div>
  );
}
